import { Drawer } from '@/components';
import { useProject } from '@/features/project/_hooks/project';
import type { PreviousFiscalYearExecutedFormSchema } from '@/features/project/projects/_models/segment-expenditure-and-execution';
import { useConsecutiveFiscalYears } from '@/features/project/projects/detail/activities/_hooks/consecutive';
import {
  useCreateSegmentExpenditureAndExecution,
  useSegmentExpenditureAndExecution,
  useUpdateSegmentExpenditureAndExecution,
} from '@/features/project/projects/detail/income-and-expenditure/_hooks/segment-expenditure-and-execution';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useProjectDetailDrawerState } from '../../../_components/ProjectDrawerStateProvider';
import { PreviousFiscalYearExecutedForm } from './PreviousFiscalYearExecutedForm';

export const PreviousFiscalYearExecutedFormDrawer: FC = () => {
  const { t } = useTranslation();
  const { id: projectId } = useId();
  const consecutiveFiscalYears = useConsecutiveFiscalYears({ id: projectId });

  const project = useProject(projectId);
  const preYearProjectId = consecutiveFiscalYears.data?.find(
    (v) => v.fiscalYear === (project.data?.fiscalYear ?? 0) - 1,
  )?.id;

  const segmentExpenditureAndExecution = useSegmentExpenditureAndExecution({
    projectId,
  });

  const { drawerState, setDrawerState } = useProjectDetailDrawerState();
  const { openMessage } = useMessage();
  const isOpenDraw =
    drawerState.open && drawerState.type === 'ss-previous-fiscal-year-executed';
  const handleClose = async () => {
    setDrawerState({ open: false });
  };

  const { mutateAsync: createMutateAsync } =
    useCreateSegmentExpenditureAndExecution();
  const { mutateAsync: updateMutateAsync } =
    useUpdateSegmentExpenditureAndExecution();

  const initialSegmentExpenditureAndExecution =
    segmentExpenditureAndExecution.data?.find(
      (v) => v.fiscalYear === (project.data?.fiscalYear ?? 0) - 1,
    );

  return (
    <Drawer open={isOpenDraw} onClose={handleClose} destroyOnClose>
      <PreviousFiscalYearExecutedForm
        initialValues={initialSegmentExpenditureAndExecution}
        onSubmit={async (
          values: z.infer<typeof PreviousFiscalYearExecutedFormSchema>,
        ) => {
          if (initialSegmentExpenditureAndExecution) {
            await updateMutateAsync({
              params: {
                ...values,
                projectId: preYearProjectId,
                id: initialSegmentExpenditureAndExecution.id,
                skipLock: true,
              },
            });
          } else {
            await createMutateAsync({
              params: {
                ...values,
                projectId: preYearProjectId,
                skipLock: true,
              },
            });
          }
          await Promise.all([
            openMessage('success', t('message-update-completed')),
            handleClose(),
          ]);
        }}
      />
    </Drawer>
  );
};
